import { ComponentRef, Injectable, ViewContainerRef, inject } from '@angular/core';
import { ComponentType } from '@angular/cdk/overlay';
import { BrnDialogRef, BrnDialogService } from '@spartan-ng/brain/dialog';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AlertDialogComponent,
  AlertDialogContext,
} from '../components/ui/dialog/alert-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogContext,
} from '../components/ui/dialog/confirm-dialog.component';

export interface DialogConfig<TContext = Record<string, unknown>> {
  /** Passed as context to the dynamic component via injectBrnDialogContext() */
  context?: TContext;
  /** Override the dialog panel CSS width (e.g. '40rem', '600px') */
  width?: string;
  /** Whether to show fullscreen on mobile (< 1024px). Defaults to true. */
  fullscreenOnMobile?: boolean;
  /** Whether clicking outside / pressing Escape closes the dialog. Defaults to true. */
  closable?: boolean;
}

/**
 * Programmatic dialog service using Spartan brain (BrnDialogService).
 *
 * @example — open a custom component
 * ```typescript
 * const ref = this.dialogService.open(MyComponent, { context: { userId: '123' } });
 * ref.closed$.subscribe(result => console.log('result:', result));
 * ```
 *
 * @example — confirm dialog
 * ```typescript
 * this.dialogService.confirm({
 *   title: 'Delete item?',
 *   description: 'This action cannot be undone.',
 *   confirmText: 'Delete',
 *   confirmVariant: 'destructive',
 * }).subscribe(confirmed => { if (confirmed) this.doDelete(); });
 * ```
 *
 * @example — alert dialog
 * ```typescript
 * this.dialogService.alert({ title: 'Success', description: 'Saved.' });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly _brnDialogService = inject(BrnDialogService);

  /**
   * Open any Angular component as a dialog.
   * The component can inject its context with `injectBrnDialogContext<MyContext>()`.
   *
   * @param component  The Angular component class to render as dialog content.
   * @param config     Optional config: context data, width, mobile fullscreen, closable.
   * @param vcr        Optional `ViewContainerRef` for proper dependency injection tree.
   * @returns          `BrnDialogRef` — use `.closed$` to react to close events.
   */
  open<TContext = Record<string, unknown>>(
    component: ComponentType<unknown>,
    config?: DialogConfig<TContext>,
    vcr?: ViewContainerRef,
  ): BrnDialogRef<any> {
    return this._brnDialogService.open(
      component,
      vcr,
      config?.context,
      {
        closeOnOutsidePointerEvents: config?.closable ?? true,
        closeOnBackdropClick: config?.closable ?? true,
        hasBackdrop: true,
      },
    );
  }

  /**
   * Open a confirmation dialog and return an Observable<boolean>.
   * Resolves `true` when the user confirms, `false` when they cancel/close.
   *
   * @param config  Confirmation dialog options.
   * @param vcr     Optional ViewContainerRef.
   */
  confirm(
    config: ConfirmDialogContext & Pick<DialogConfig, 'closable'> = {},
    vcr?: ViewContainerRef,
  ): Observable<boolean> {
    const ctx: ConfirmDialogContext = {
      title: config.title ?? 'Are you sure?',
      description: config.description,
      confirmText: config.confirmText ?? 'Confirm',
      cancelText: config.cancelText ?? 'Cancel',
      confirmVariant: config.confirmVariant ?? 'default',
      fullscreenOnMobile: config.fullscreenOnMobile ?? true,
    };

    const ref = this._brnDialogService.open(
      ConfirmDialogComponent,
      vcr,
      ctx,
      {
        closeOnOutsidePointerEvents: config.closable ?? true,
        closeOnBackdropClick: config.closable ?? true,
        hasBackdrop: true,
      },
    );

    return from(ref.closed$).pipe(
      map((result) => result === true),
    );
  }

  /**
   * Open a simple alert dialog with an OK button.
   *
   * @param config  Alert dialog options.
   * @param vcr     Optional ViewContainerRef.
   */
  alert(
    config: AlertDialogContext = {},
    vcr?: ViewContainerRef,
  ): Observable<void> {
    const ctx: AlertDialogContext = {
      title: config.title ?? 'Notice',
      description: config.description,
      okText: config.okText ?? 'OK',
      fullscreenOnMobile: config.fullscreenOnMobile ?? true,
    };

    const ref = this._brnDialogService.open(
      AlertDialogComponent,
      vcr,
      ctx,
      {
        closeOnOutsidePointerEvents: false,
        closeOnBackdropClick: false,
        hasBackdrop: true,
      },
    );

    return from(ref.closed$).pipe(map(() => undefined));
  }

  /**
   * Close all open dialogs.
   * Note: BrnDialogService does not expose a closeAll() API directly;
   * individual refs should be tracked if needed. This is a no-op placeholder
   * for future use.
   */
  closeAll(): void {
    // BrnDialogService does not expose a global closeAll.
    // Track refs individually if needed.
  }
}
